import { useMemo, useState } from 'react';

import { toast } from 'react-toastify';
import styled from 'styled-components';

import {
    LocationState,
    LocationType,
    useAssignLocationToProductMutation,
    useGetLocationsByWarehouseColumnIdQuery,
} from 'data/__generated__';

import { TotemPopup } from 'components/TotemPopup';
import { TotemPrimaryButton } from 'components/TotemPrimaryButton';
import { TotemSelect } from 'components/TotemSelect';
import { WarehouseColumnSelect } from 'components/Select/WarehouseColumnSelect';

type Props = {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    productId: string;
};

const LOCATION_TYPE_OPTIONS = [
    { label: 'Picking', value: LocationType.Picking },
    { label: 'Réserve', value: LocationType.Reserve },
];

export const AddLocationPopup = ({ isOpen, setIsOpen, productId }: Props) => {
    const [selectedWarehouseColumnId, setSelectedWarehouseColumnId] = useState<string | null>(null);
    const [selectedDatamatrix, setSelectedDatamatrix] = useState<string | null>(null);
    const [selectedLocationType, setSelectedLocationType] = useState<LocationType>(LocationType.Picking);

    const { data: locationsData, loading: locationsLoading } = useGetLocationsByWarehouseColumnIdQuery({
        variables: { warehouseColumnId: selectedWarehouseColumnId! },
        skip: !selectedWarehouseColumnId,
    });

    const [assignLocation, { loading: assignLoading }] = useAssignLocationToProductMutation();

    const locationOptions = useMemo(() => {
        if (!locationsData) return [];
        return locationsData.locationsByWarehouseColumnId
            .filter((loc) => loc.state === LocationState.Regular)
            .sort((a, b) => a.index - b.index)
            .map((loc) => ({
                label: loc.productId ? `${loc.datamatrix} — ${loc.product?.name || 'Produit inconnu'}` : loc.datamatrix,
                value: loc.datamatrix,
                isDisabled: !!loc.productId,
            }));
    }, [locationsData]);

    const resetAndClose = () => {
        setSelectedWarehouseColumnId(null);
        setSelectedDatamatrix(null);
        setSelectedLocationType(LocationType.Picking);
        setIsOpen(false);
    };

    const handleWarehouseChange = (warehouseColumnId: string | null) => {
        setSelectedWarehouseColumnId(warehouseColumnId);
        setSelectedDatamatrix(null);
    };

    const handleSubmit = async () => {
        if (!selectedWarehouseColumnId || !selectedDatamatrix) return;

        const { data } = await assignLocation({
            variables: {
                productId,
                datamatrix: selectedDatamatrix,
                warehouseColumnId: selectedWarehouseColumnId,
                locationType: selectedLocationType,
            },
            refetchQueries: ['GET_DETAILED_PRODUCT'],
        });

        if (data?.assignLocationToProduct?.error) {
            toast.error(data.assignLocationToProduct.error);
        } else if (data?.assignLocationToProduct?.location) {
            toast.success(`Emplacement ${selectedDatamatrix} assigné`);
            resetAndClose();
        }
    };

    return (
        <TotemPopup isOpen={isOpen} setIsOpen={resetAndClose} title="Ajouter un emplacement" contentOverflow="visible">
            <PopupContent>
                <WarehouseColumnSelect
                    value={selectedWarehouseColumnId}
                    onChange={handleWarehouseChange}
                    isClearable={false}
                />
                {selectedWarehouseColumnId ? (
                    <TotemSelect
                        label="Emplacement"
                        placeholder={locationsLoading ? 'Chargement...' : 'Sélectionner un emplacement'}
                        options={locationOptions}
                        value={selectedDatamatrix}
                        onChange={(value) => setSelectedDatamatrix(value)}
                        isClearable
                        isMulti={false}
                    />
                ) : null}
                <TotemSelect
                    label="Type"
                    options={LOCATION_TYPE_OPTIONS}
                    value={selectedLocationType}
                    onChange={(value) => setSelectedLocationType(value || LocationType.Picking)}
                    isMulti={false}
                />
                <TotemPrimaryButton
                    type="button"
                    disabled={!selectedDatamatrix || assignLoading}
                    onClick={handleSubmit}
                >
                    Assigner
                </TotemPrimaryButton>
            </PopupContent>
        </TotemPopup>
    );
};

const PopupContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 15px;
    min-width: 400px;
    padding: 10px 0;
`;
